import React, { useEffect, useState } from 'react';
import HeartIcon from '../../../../assets/heart-reaction-icon.svg';
import SadIcon from '../../../../assets/sad-reaction-icon.svg';
import AngryIcon from '../../../../assets/angry-reaction-icon.svg';
import FunnyIcon from '../../../../assets/funny-reaction-icon.svg';
import BrainIcon from '../../../../assets/galaxy-brain-reaction-icon.svg';
import InsightfulIcon from '../../../../assets/insightful-reaction-icon.svg';
import CloseIcon from '../../../../assets/close-modal-icon.svg';
import CommentGreenIcon from '../../../../assets/comment-green-icon.svg';
import { formatNumber, handleUserClick } from '../../../../utils/helper.util';
import NeedsCitationIcon from '../../../../assets/needs-citation.svg';
import BadLogicIcon from '../../../../assets/puzzle.svg';
import { getArticleReactionUsers } from '../../../../apis/article.api';
import { useNavigate } from 'react-router-dom';
import VerifiedIcon from '../../../../assets/verified-icon.svg';
import UserAvatar from '../../../../assets/medium_default_optimized.png';

interface ReactionModalProps {
  isOpen: boolean;
  onClose: () => void;
  reactions: any;
  totalComment: number;
  articleId: string;
}

const REACTIONS = [
  { type: 'all', textDisplay: 'All', key: 'total' },
  { type: 'like', icon: HeartIcon, textDisplay: 'Like', key: 'like' },
  { type: 'funny', icon: FunnyIcon, textDisplay: 'Funny', key: 'funny' },
  { type: 'angry', icon: AngryIcon, textDisplay: 'Angry', key: 'angry' },
  { type: 'sad', icon: SadIcon, textDisplay: 'Sad', key: 'sad' },
  { type: 'innovative', icon: InsightfulIcon, textDisplay: 'Insightful', key: 'innovative' },
  { type: 'big_brain', icon: BrainIcon, textDisplay: 'Big Brain', key: 'big_brain' },
  { type: 'need_source', icon: NeedsCitationIcon, textDisplay: 'Needs Source', key: 'need_source' },
  { type: 'poor_logic', icon: BadLogicIcon, textDisplay: 'Poor Logic', key: 'poor_logic' }
];

const ArticleReactionStatsModal: React.FC<ReactionModalProps> = ({
  onClose,
  reactions,
  totalComment,
  articleId
}) => {
  const [reactionUsers, setReactionUsers] = useState<any[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [selectedTab, setSelectedTab] = useState('summary');
  const [selectedReactionFilter, setSelectedReactionFilter] = useState<string>('all');
  const [usersPagination, setUsersPagination] = useState({
    page: 1,
    limit: 20,
    totalPages: 1,
    hasNextPage: false
  });

  const navigate = useNavigate();

  const fetchReactionUsers = async (reactionType?: string, page: number = 1) => {
    try {
      setIsLoadingUsers(true);
      const response = await getArticleReactionUsers(articleId, {
        reactionType: reactionType === 'all' ? undefined : reactionType,
        page,
        limit: 20
      });

      // Handle the response structure - it might be nested differently
      const userData = response?.data?.data || response?.data || response;
      const paginationData = response?.data?.pagination || response?.pagination;

      if (userData) {
        console.log('Setting reaction users:', userData);
        if (page === 1) {
          setReactionUsers(userData);
        } else {
          setReactionUsers((prev) => [...prev, ...userData]);
        }
        if (paginationData) {
          setUsersPagination(paginationData);
        }
      } else {
        console.log('No user data found in response');
      }
    } catch (err) {
      console.error('Failed to load reaction users:', err);
    } finally {
      setIsLoadingUsers(false);
    }
  };

  useEffect(() => {
    if (articleId && selectedReactionFilter) {
      fetchReactionUsers(selectedReactionFilter, 1);
    }
  }, [selectedReactionFilter, articleId]);

  // Initial load
  useEffect(() => {
    if (articleId) {
      fetchReactionUsers('all', 1);
    }
  }, [articleId, reactions]);

  console.log(reactions);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100]">
      <div className="p-8 bg-white rounded-lg w-full max-w-[673px]">
        <div className="flex justify-between items-center pb-2 border-b border-[#D1C8C4]">
          <h3 className="text-xl font-[800] text-gray-900">Reactions and Comments</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-full">
            <img src={CloseIcon} alt="Close" className="w-8 h-8" />
          </button>
        </div>

        <p className="text-md mt-4">
          Review a breakdown of all interactions made on this{' '}
          <span className="font-bold">article</span>:
        </p>

        {/* Tab Navigation */}
        <div className="flex border-b border-gray-200 mb-4 mt-4">
          <button
            className={`px-4 py-2 text-md ${
              selectedTab === 'summary'
                ? 'text-growth-green-600 border-b-2 border-growth-green-500 font-[800]'
                : 'text-gray-500 hover:text-gray-700 font-[600]'
            }`}
            onClick={() => setSelectedTab('summary')}
          >
            Summary
          </button>
          <button
            className={`px-4 py-2 text-md ${
              selectedTab === 'users'
                ? 'text-growth-green-600 border-b-2 border-growth-green-500 font-[800]'
                : 'text-gray-500 hover:text-gray-700 font-[600]'
            }`}
            onClick={() => setSelectedTab('users')}
          >
            Users
          </button>
        </div>

        {selectedTab === 'summary' ? (
          <>
            <p className="text-md mb-4 font-bold">Article's Reaction Summary</p>

            {/* Summary Section */}
            <div className="grid grid-cols-3 gap-4 bg-green-50 p-4 rounded-lg mb-6">
              <div className="text-left bg-[white] rounded-lg border-[1px] border-[#F2F3F7] p-6">
                <div className="text-gray-800 text-sm font-bold">Total Interactions:</div>
                <div className="text-lg font-bold break-keep whitespace-nowrap">
                  {formatNumber(
                    reactions
                      ? (Object.values(reactions as Record<string, number>).reduce(
                          (sum, value) => sum + value,
                          0
                        ) || 0) + totalComment
                      : 0
                  )}
                </div>
              </div>
              <div className="text-left bg-[white] rounded-lg border-[1px] border-[#F2F3F7] p-6">
                <div className="text-gray-800 text-sm font-bold">Total Comments:</div>
                <div className="flex flex-col md:flex-row items-start gap-1 break-keep whitespace-nowrap">
                  <img src={CommentGreenIcon} alt="Close" className="w-6 h-6" />
                  <p className="text-lg inline font-bold">{formatNumber(totalComment || 0)}</p>
                  <p className="text-sm mt-1 md:inline hidden">Comments</p>
                </div>
              </div>
              <div className="text-left bg-[white] rounded-lg border-[1px] border-[#F2F3F7] p-6">
                <div className="text-gray-800 text-sm font-bold mb-2">Top Reactions:</div>
                <div className="flex flex-col gap-2 break-keep whitespace-nowrap">
                  {REACTIONS.filter((r) => r.type !== 'all')
                    .map((r) => ({ ...r, count: reactions?.[r.key] || 0 }))
                    .filter((r) => r.count > 0) // Changed from >= 0 to > 0
                    .sort((a, b) => b.count - a.count)
                    .slice(0, 2)
                    .map((r) => (
                      <div key={r.key} className="flex items-center gap-2 text-sm">
                        <img src={r.icon} alt={r.textDisplay} className="w-4 h-4 flex-shrink-0" />
                        <span className="text-lg font-bold">{r.count}</span>
                        <span className="text-sm truncate md:block hidden">{`${r.textDisplay}${r.count > 1 ? 's' : ''}`}</span>
                        <span className="text-sm truncate md:hidden block">{`${r.count > 1 ? 's' : ''}`}</span>
                      </div>
                    ))}
                  {/* Show "No reactions yet" if no reactions */}
                  {REACTIONS.filter((r) => r.type !== 'all')
                    .map((r) => ({ ...r, count: reactions?.[r.key] || 0 }))
                    .filter((r) => r.count > 0).length === 0 && (
                    <div className="text-sm text-gray-500 italic">No reactions yet</div>
                  )}
                </div>
              </div>
            </div>

            {/* Distribution Section */}
            <h4 className="font-bold text-base mb-2">Section’s Reaction Distribution</h4>
            <div className="space-y-3 border-[1px] border-[#EAECF0] p-4 rounded-lg">
              {REACTIONS.filter((r) => r.type !== 'all').map((reaction) => {
                const count = reactions?.[reaction.key] || 0;
                const totalReactions = REACTIONS.reduce(
                  (sum, r) => (r.key !== 'total' ? sum + (reactions?.[r.key] || 0) : sum),
                  0
                );
                const percentage =
                  totalReactions > 0 ? Math.round((count / totalReactions) * 100) : 0;

                return (
                  <div key={reaction.type} className="flex items-center justify-between">
                    <div className="flex items-center gap-2 w-[120px]">
                      <img src={reaction.icon} alt={reaction.textDisplay} className="w-6 h-6" />
                      <span>{reaction.textDisplay}</span>
                    </div>
                    <div className="flex-1 mx-4 bg-gray-200 rounded-full h-4">
                      <div
                        className="h-4 bg-growth-green-500 rounded-full"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                    <div className="text-sm text-gray-700 w-[50px] text-right whitespace-nowrap">
                      {count} ({percentage}%)
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        ) : selectedTab === 'users' ? (
          /* Users view */
          <div>
            <h4 className="text-md font-bold mb-3">Users Who Reacted</h4>

            {/* Filter by reaction type */}
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">Filter by reaction:</p>
              <div className="flex flex-wrap gap-2">
                <button
                  className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                    selectedReactionFilter === 'all'
                      ? 'bg-growth-green-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  onClick={() => setSelectedReactionFilter('all')}
                >
                  All (
                  {formatNumber(
                    Object.values(reactions as Record<string, number>).reduce(
                      (sum, value) => sum + value,
                      0
                    ) || 0
                  )}
                  )
                </button>
                {REACTIONS.slice(1)
                  .filter((r) => {
                    const count = reactions?.[r.key] || 0;
                    console.log(`Reaction ${r.key}:`, count);
                    return count > 0;
                  })
                  .map((reaction) => (
                    <button
                      key={reaction.key}
                      className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors flex items-center gap-1 ${
                        selectedReactionFilter === reaction.key
                          ? 'bg-growth-green-500 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                      onClick={() => setSelectedReactionFilter(reaction.key)}
                    >
                      <img src={reaction.icon} alt={reaction.textDisplay} className="w-4 h-4" />
                      {formatNumber(reactions?.[reaction.key] || 0)}
                    </button>
                  ))}
              </div>
            </div>

            {/* Users list */}
            <div className="max-h-96 overflow-y-auto">
              {isLoadingUsers && reactionUsers.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-4 border-growth-green-500 border-t-transparent"></div>
                  <p className="mt-2 text-gray-600">Loading users...</p>
                </div>
              ) : reactionUsers.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8">
                  <p className="text-gray-500">No users found for this reaction</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {reactionUsers.map((userReaction, index) => (
                    <div
                      key={`${userReaction.user.id}-${index}`}
                      className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={(e) =>
                        handleUserClick(e, () => navigate(`/${userReaction.user.username}`))
                      }
                    >
                      <div className="flex items-center gap-3">
                        <img
                          src={userReaction.user.imgUrl || UserAvatar}
                          alt={userReaction.user.firstName}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm truncate">
                              {userReaction.user.firstName}
                            </span>
                            {userReaction.user.isVerifiedAuthor && (
                              <img src={VerifiedIcon} alt="verified" className="w-3.5 h-3.5" />
                            )}
                            {userReaction.user.username && (
                              <span className="text-xs text-gray-500 truncate">
                                @{userReaction.user.username}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <img
                            src={REACTIONS.find((r) => r.key === userReaction.reactionType)?.icon}
                            alt={userReaction.reactionTypeDisplay}
                            className="w-5 h-5"
                          />
                          <span className="text-xs bg-growth-green-100 text-growth-green-800 px-2 py-1 rounded-full font-medium">
                            {userReaction.reactionTypeDisplay}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Load more button */}
                  {usersPagination.hasNextPage && (
                    <button
                      className="w-full py-2 px-4 border border-growth-green-500 text-growth-green-600 rounded-lg hover:bg-growth-green-50 transition-colors font-medium"
                      onClick={() =>
                        fetchReactionUsers(selectedReactionFilter, usersPagination.page + 1)
                      }
                      disabled={isLoadingUsers}
                    >
                      {isLoadingUsers ? (
                        <div className="flex items-center justify-center gap-2">
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-growth-green-500 border-t-transparent"></div>
                          Loading more...
                        </div>
                      ) : (
                        `Load More (${usersPagination.totalPages - usersPagination.page} pages remaining)`
                      )}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default ArticleReactionStatsModal;
